// src/api/_axios.ts
import axios, { AxiosError } from 'axios';
import useAuthStore from '../store/authStore';
import { useTeamStore, isOwnAccountCall } from '../store/useTeamStore';
import { deriveApiUrl } from './apiBase';

// Same-origin on the deployed dashboard so operator browsers never hit the raw
// API server IP (some ISPs drop TCP to it). See apiBase.ts + vercel.json.
const API_URL = deriveApiUrl();

const apiClient = axios.create({
   baseURL: API_URL,
   headers: {
      'Content-Type': 'application/json',
   },
});

// Request interceptor to add token
apiClient.interceptors.request.use(
   (config) => {
      const token = useAuthStore.getState().token;

      /*
       * 🔴 NEVER CLOBBER A HEADER THE CALLER SET. The subscriber bill page
       * passes its own portal token per request; this used to overwrite it
       * with the logged-in SmartFi user's token, so `/isp/portal/me` answered
       * 401 and the customer was thrown back to the sign-in card for ever.
       * Measured on staging 2026-09-03: the header sent WAS the SmartFi token,
       * not the portal one. An operator could never open their own bill page.
       */
      if (token && !config.headers.Authorization) {
         config.headers.Authorization = `Bearer ${token}`;
      }
      // Team access: working on someone else's account (Ray 2026-10-04).
      // Never on my own sign-in/profile calls or my own team invites.
      const acting = useTeamStore.getState().acting;
      if (acting && !config.headers['X-Act-As'] && !isOwnAccountCall(String(config.url ?? ''))) {
         config.headers['X-Act-As'] = acting.ownerId;
      }
      return config;
   },
   (error) => Promise.reject(error)
);

/**
 * A call made on behalf of an ISP SUBSCRIBER, not a SmartFi user.
 *
 * 🔴 THE GLOBAL 401 REDIRECT MUST NEVER FIRE FOR THESE. The bill page is
 * public and its reader has no SmartFi account — they signed in with a phone
 * number to their own provider. Throwing them to `/` lands them on the SmartFi
 * login, which means nothing to them and loses the bill they came to pay.
 * Ray, 2026-09-03: *"after redirect, in a few seconds it will redirect to app
 * smartfi login."* Their portal token had simply gone stale.
 *
 * It also protects an OPERATOR: clearAuth() here would sign them out of their
 * own dashboard because a subscriber's token expired in another tab.
 *
 * The portal handles its own 401 — it shows the phone-number card again.
 */
const isPortalCall = (error: AxiosError) =>
   String(error.config?.url ?? '').includes('/isp/portal');

// Response interceptor for global error handling (optional)
apiClient.interceptors.response.use(
   (response) => response,
   (error: AxiosError) => {
      // Global error handling (can be customized)
      if (error.response?.status === 401 && !isPortalCall(error)) {
         // Example: Handle unauthorized errors
         useAuthStore.getState().clearAuth();
         localStorage.removeItem('x-user'); // Clear legacy token if used
         window.location.href = '/'; // Force redirect
      }
      // Surface server-side error messages to `error.message` so toast
      // calls that read `error.message` (the common pattern across this
      // codebase) show the actual reason (e.g. "Minimum payout amount is
      // ₱100.") instead of axios's generic "Request failed with status
      // code 400". Server returns one of { err, error, msg } depending
      // on the controller — check all three.
      const data = error.response?.data as
         | { err?: string; error?: string; msg?: string }
         | undefined;
      const serverMsg = data?.err || data?.error || data?.msg;
      if (serverMsg) {
         error.message = serverMsg;
      }

      // The server refuses every data route for an unverified account with a
      // 403 "Please verify your email". If the stored account copy still says
      // verified (or says nothing), the verify gate can't know — so teach it
      // from the answer itself. This makes the verify screen appear on the
      // very first rejected call, whatever page the user is on.
      if (
         error.response?.status === 403 &&
         typeof serverMsg === 'string' &&
         /verify your email/i.test(serverMsg)
      ) {
         const { token, user, setAuth } = useAuthStore.getState();
         if (token && user && user.verified !== false) {
            setAuth(token, { ...user, verified: false });
         }
      }
      // Team access: the owner removed this helper (or the account can no
      // longer be shared) → drop back to my own account instead of failing
      // every call on their dashboard.
      const code = (error.response?.data as { code?: string } | undefined)?.code;
      if (
         error.response?.status === 403 &&
         code === 'TEAM_DENIED' &&
         typeof serverMsg === 'string' &&
         /no longer have access|cannot be shared/i.test(serverMsg) &&
         useTeamStore.getState().acting
      ) {
         useTeamStore.getState().setActing(null);
         window.location.href = '/wifi';
      }
      return Promise.reject(error);
   }
);

export default apiClient;
